import { useState } from "react";
import { S3Client } from "@aws-sdk/client-s3";
import { Progress, Upload } from "@aws-sdk/lib-storage";
import { v4 as uuid } from "uuid";
import { XhrHttpHandler } from "@aws-sdk/xhr-http-handler";
import { AWS_REGION } from "constants/aws";
import updateRecordingS3Key from "services/transcription/updateRecordingS3Key";

type UseRecordingUpload = () => {
  uploadFile: (
    file: File,
    bucket: string,
    credentials: AWSCredentials,
    appointmentId?: string,
  ) => void;
  uploadComplete: boolean;
  uploadProgress: number;
  s3Key: string | null;
  error: string | null;
  reset: () => void;
  fileName: string;
  fileSize: string;
};

type AWSCredentials = {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken: string;
};

const SUPPORTED_FILE_TYPES = [
  "audio/mpeg",
  "video/mp4",
  "audio/mp4",
  "audio/x-m4a",
  "audio/ogg",
  "audio/wav",
  "audio/x-wav",
  "video/webm",
  "audio/webm",
  "video/quicktime",
];

const useRecordingUpload: UseRecordingUpload = () => {
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploadComplete, setUploadComplete] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [s3Key, setS3Key] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>("");
  const [fileSize, setFileSize] = useState<string>("");

  const onUploadProgress = (progressEvent: Progress) => {
    if (progressEvent.loaded && progressEvent.total) {
      setUploadProgress(
        Math.round((progressEvent.loaded / progressEvent.total) * 100),
      );
    }
  };

  const uploadFile = async (
    file: File,
    bucket: string,
    credentials: AWSCredentials,
    appointmentId?: string,
  ) => {
    setError(null);
    reset();

    if (!SUPPORTED_FILE_TYPES.includes(file.type)) {
      setError("Unsupported file type. Please use a valid file type");
      return;
    }

    setFileName(file.name);
    setFileSize(`${String(Math.round(file.size / 100000) / 10)}mb`);

    const client = new S3Client({
      region: AWS_REGION,
      credentials,
      // We use the XhrHttpHandler instead of the default fetch handler
      // because the default does not support upload progress
      requestHandler: new XhrHttpHandler({}),
    });
    const key = `${uuid()}-${file.name}`;

    try {
      const parallelUploads3 = new Upload({
        client,
        params: {
          Bucket: bucket,
          Key: key,
          Body: file,
        },
      });

      parallelUploads3.on("httpUploadProgress", (progress) => {
        onUploadProgress(progress);
      });

      // In some cases we don't have an appointment id (like when we are uploading the file before
      // creating the appointment). In those cases, the s3 key is persisted after the upload. In
      // all other cases, we persist the s3 key before the actual upload
      if (appointmentId) {
        await updateRecordingS3Key(appointmentId, key);
      }
      await parallelUploads3.done();
    } catch (e) {
      console.error(e);
      setError("Something went wrong. Try uploading again");
      return;
    }

    setS3Key(key);
    setUploadProgress(100);
    setUploadComplete(true);
  };

  const reset = () => {
    setS3Key(null);
    setError(null);
    setUploadProgress(0);
    setUploadComplete(false);
  };

  return {
    uploadFile,
    uploadProgress,
    uploadComplete,
    reset,
    error,
    s3Key,
    fileName,
    fileSize,
  };
};

export default useRecordingUpload;
